import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, apiGet, apiPatch } from "../lib/api.js";

/**
 * How the live form behaves — identity, drafts, the review step, bot protection, and whether
 * applicants may correct their answers afterwards.
 *
 * These were checkboxes in the Settings tab that did nothing at all. They wrote to the draft
 * version's `settings` blob, which is snapshotted at publish and read back into this editor but
 * never consulted at runtime — the behaviour is governed by columns on the form row, and nothing
 * in the builder wrote those. They read back from the blob too, so an absent key rendered as
 * ticked: a form with Turnstile switched off through the API still showed bot protection on.
 *
 * So this talks to `PATCH /forms/:id` directly, the same way `AvailabilityPanel` owns its own
 * endpoint, and reads the row rather than the snapshot.
 */

interface FormBehaviour {
  requiresIdentity: boolean;
  allowDrafts: boolean;
  reviewBeforeSubmit: boolean;
  turnstileEnabled: boolean;
  allowEditAfterSubmit: boolean;
  editWindowHours: number | null;
  status: string;
}

const FLAGS: { key: keyof FormBehaviour; label: string; note: string }[] = [
  {
    key: "requiresIdentity",
    label: "Require email verification before the form can be filled",
    note: "Off means anyone with the link can submit, and nothing links a submission to a person — no prefill next year, and no one-per-applicant rule.",
  },
  {
    key: "allowDrafts",
    label: "Let applicants save and resume",
    note: "Answers are mirrored to their own device either way; this is the copy held on the server, which survives losing the phone.",
  },
  {
    key: "reviewBeforeSubmit",
    label: "Show a review step before submitting",
    note: "A last read-through of every answer. Worth keeping on a long form.",
  },
  {
    key: "turnstileEnabled",
    label: "Bot protection (Cloudflare Turnstile)",
    note: "A real download on a slow connection, but the thing standing between a public form and scripted submissions.",
  },
];

export const FormBehaviourPanel = ({ formId }: { formId: string }): JSX.Element => {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<FormBehaviour | null>(null);
  const [limitHours, setLimitHours] = useState("");
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formQuery = useQuery({
    queryKey: ["form-behaviour", formId],
    queryFn: () => apiGet<FormBehaviour>(`/forms/${formId}`),
  });

  useEffect(() => {
    if (!formQuery.data) return;
    setDraft(formQuery.data);
    setLimitHours(formQuery.data.editWindowHours?.toString() ?? "");
  }, [formQuery.data]);

  const save = useMutation({
    mutationFn: () =>
      apiPatch(`/forms/${formId}`, {
        requiresIdentity: draft!.requiresIdentity,
        allowDrafts: draft!.allowDrafts,
        reviewBeforeSubmit: draft!.reviewBeforeSubmit,
        turnstileEnabled: draft!.turnstileEnabled,
        allowEditAfterSubmit: draft!.allowEditAfterSubmit,
        editWindowHours: limitHours.trim() ? Number(limitHours) : null,
      }),
    onSuccess: async () => {
      setError(null);
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: ["form-behaviour", formId] });
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : "Could not save that."),
  });

  if (!draft) return <p className="text-xs text-muted-foreground">Loading…</p>;

  const set = <K extends keyof FormBehaviour>(key: K, value: FormBehaviour[K]): void => {
    setDraft({ ...draft, [key]: value });
    setSaved(false);
  };

  return (
    <div className="space-y-4">
      {draft.status === "PUBLISHED" && (
        // Not part of the draft, so publishing is not what applies it. Said plainly, because
        // these sit inside a draft editor where everything else waits for publish.
        <p className="rounded-md border border-amber-400 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          This form is live. Saving here changes it for the next applicant immediately — these are
          not part of the draft and do not wait for publish.
        </p>
      )}

      <div className="space-y-2.5">
        {FLAGS.map((flag) => (
          <label key={flag.key} className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={draft[flag.key] as boolean}
              onChange={(event) => set(flag.key, event.target.checked as never)}
              className="mt-1"
            />
            <span>
              <span className="font-medium">{flag.label}</span>
              <span className="block text-xs text-muted-foreground">{flag.note}</span>
            </span>
          </label>
        ))}
      </div>

      <div className="border-t pt-3">
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={draft.allowEditAfterSubmit}
            onChange={(event) => set("allowEditAfterSubmit", event.target.checked)}
            className="mt-1"
          />
          <span>
            <span className="font-medium">
              Let applicants change their answers after submitting
            </span>
            <span className="block text-xs text-muted-foreground">
              While this form is open. Their reference number and the address they verified stay
              fixed; everything else they can correct themselves. Closing the form ends it.
            </span>
          </span>
        </label>

        {draft.allowEditAfterSubmit && (
          <label className="ml-6 mt-2 block text-sm">
            <span className="mb-1 block text-[11px] uppercase tracking-wide text-muted-foreground">
              Stop sooner, this many hours after they submit (optional)
            </span>
            <input
              type="number"
              min={1}
              value={limitHours}
              onChange={(event) => {
                setLimitHours(event.target.value);
                setSaved(false);
              }}
              placeholder="Until the form closes"
              className="w-56 rounded-md border px-2 py-1.5 text-sm"
            />
          </label>
        )}
      </div>

      {error && <p className="text-xs text-destructive">{error}</p>}

      <div className="flex items-center gap-3">
        <button
          type="button"
          disabled={save.isPending}
          onClick={() => save.mutate()}
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
        >
          {save.isPending ? "Saving…" : "Save"}
        </button>
        {saved && <span className="text-xs text-muted-foreground">Saved.</span>}
      </div>
    </div>
  );
};
